/**
 * DemoModeBanner.jsx
 * ──────────────────
 * Sticky top banner rendered ONLY on demo.<module>.au subdomains
 * (or when ?as=demo is set in the URL). Non-intrusive — 32px tall,
 * yellow gradient, dismissable per-session. Includes a "Sign in as
 * demo user" pill that POSTs to /api/auth/demo-login and reloads.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { isDemoMode, demoModuleKey } from "@/lib/demoMode";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");
const DISMISS_KEY = "hp-demo-banner-dismissed";

export default function DemoModeBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isDemoMode()) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch { /* ignore */ }
    setVisible(true);
  }, []);

  if (!visible) return null;

  const signInAsDemo = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/demo-login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ module: demoModuleKey() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`Signed in as ${data.name || "demo user"}`);
      // Reload so AuthContext re-fetches /auth/me and every widget sees
      // the demo user immediately.
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error("Demo login failed — try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      data-testid="demo-mode-banner"
      style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "linear-gradient(90deg,#fef08a 0%,#fde68a 50%,#fef08a 100%)",
        color: "#78350f", fontSize: 13, fontWeight: 600,
        padding: "8px 16px", display: "flex", alignItems: "center",
        gap: 12, flexWrap: "wrap", justifyContent: "center",
        borderBottom: "1px solid #f59e0b33",
        fontFamily: "system-ui,-apple-system,sans-serif",
      }}
    >
      <span style={{ letterSpacing: ".04em" }}>
        DEMO MODE · Sample data, resets nightly · Nothing you do here is billed.
      </span>
      <button
        type="button"
        onClick={signInAsDemo}
        disabled={busy}
        data-testid="demo-mode-signin-btn"
        style={{
          background: "#78350f", color: "#fef08a",
          padding: "5px 14px", borderRadius: 999, border: "none",
          fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer",
          letterSpacing: ".04em",
        }}
      >
        {busy ? "Signing in…" : "Sign in as demo user"}
      </button>
      <button
        type="button"
        onClick={dismiss}
        data-testid="demo-mode-dismiss-btn"
        aria-label="Dismiss demo banner"
        style={{
          background: "transparent", color: "#78350f",
          border: "none", cursor: "pointer",
          fontWeight: 700, fontSize: 16, lineHeight: 1,
          padding: "0 4px",
        }}
      >×</button>
    </div>
  );
}
